import { useMemo, useState } from "react"
import { Plus } from "lucide-react"
import {
  useGetActiveBudgetCategoriesQuery,
  useCreateBudgetCategoryMutation,
} from "shared/redux/endpoints/procurementV2"
import { useGetProcurementCentersQuery } from "shared/redux/endpoints/procurementItems"
import { useGetDepartmentsQuery } from "shared/redux/endpoints/procurement"
import { REGION_OPTIONS } from "../constants"
import { Card, Button, Input, Select, Textarea, Currency } from "../ui"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../orderSubmitFeedback"
import { RULES, validateFields, validateOrderLine, fieldError, hasErrors } from "../fieldRules"
import OrderLineRow from "./OrderLineRow"

const emptyNewCategoryForm = { code: "", name: "", assetClassCode: "", sunPlAccount: "", sunBsAccount: "" }

let lineSeq = 0
export const newDirectLine = (centerId = "") => ({
  key: `direct-line-${(lineSeq += 1)}`,
  itemId: null,
  itemName: "",
  centerId,
  type: "new",
  replacedAssetCode: "",
  quantity: 1,
  unitPrice: 0,
  note: "",
  ticketLink: "",
  isCustomItem: false,
  customItemName: "",
  offCatalogUnit: "",
  stockReading: null,
})

export const emptyDirectSourceState = () => ({
  title: "",
  budgetCategoryId: "",
  centerId: "",
  departmentId: "",
  region: "",
  reason: "",
  lines: [newDirectLine()],
})

export const computeLinesTotal = (lines = []) =>
  lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0)

const isLineValid = (line) =>
  !!line.centerId &&
  Number(line.quantity) > 0 &&
  Number(line.unitPrice) >= 0 &&
  (line.isCustomItem ? !!line.customItemName?.trim() : !!line.itemId)

export const isDirectSourceValid = (state) =>
  !!state?.title?.trim() &&
  !!state?.budgetCategoryId &&
  !!state?.centerId &&
  (state?.lines || []).length > 0 &&
  (state.lines || []).every(isLineValid)

export const directSourceErrors = (state) => ({
  title: fieldError(state?.title, RULES.orderRequest.title),
  lines: (state?.lines || []).map(validateOrderLine),
})

export const hasDirectSourceErrors = (errs) => !!errs.title || errs.lines.some(hasErrors)

// Wire payload for POST /purchases's `directOrder` or POST
// /payment-requests's `directOrder` — same DTO both endpoints take (BE turns
// it into a hidden, already-`approved` "implicit order"). Line shape mirrors
// buildLinePayload in OrderRequestFormContainer.js (see its comment for why
// off-catalog is `offCatalogItemName`, not `isCustomItem`/`customItemName`).
export const buildDirectSourcePayload = (state) => ({
  title: state.title.trim(),
  budgetCategoryId: state.budgetCategoryId,
  centerId: state.centerId,
  departmentId: state.departmentId || undefined,
  region: state.region || undefined,
  reason: state.reason?.trim() || undefined,
  lines: (state.lines || []).map((line) => ({
    itemId: line.isCustomItem ? undefined : line.itemId,
    offCatalogItemName: line.isCustomItem ? line.customItemName?.trim() || undefined : undefined,
    offCatalogUnit: line.isCustomItem ? line.offCatalogUnit?.trim() || undefined : undefined,
    centerId: line.centerId,
    requestType: line.type ? line.type.toLowerCase() : undefined,
    replacedAssetCode: line.type === "replacement" ? line.replacedAssetCode || undefined : undefined,
    quantity: Number(line.quantity) || 0,
    unitPrice: Number(line.unitPrice) || 0,
    note: line.note || undefined,
    ticketLink: line.ticketLink?.trim() || undefined,
  })),
})

// Shared "create PO/Payment without Order/PO" (2026-09 barrier-removal
// feature) builder: title + budget category (scoped to categories that have
// the relevant flag off) + center + department + region + reason + item
// lines (reusing OrderLineRow, scoped the same way). Controlled — the parent
// owns `value` (see emptyDirectSourceState) and gets patches via `onChange`.
// `mode` picks which flag gates the category/item dropdowns: "order" for
// Create Purchase (mustHaveOrder=false), "purchase" for Create Payment
// Request (mustHavePurchase=false).
const DirectSourceBuilder = ({ mode, value, onChange, disabled, showErrors = false }) => {
  const errors = showErrors ? directSourceErrors(value) : { lines: [] }
  const flagParams = mode === "order" ? { mustHaveOrder: false } : { mustHavePurchase: false }

  const { data: categories, isFetching: categoriesLoading } = useGetActiveBudgetCategoriesQuery({
    isActive: true,
    ...flagParams,
  })

  // Inline "+ Create new category" — there may be NO category flagged for
  // this mode yet (that's exactly why this builder needs to exist at all),
  // so this can't require picking from an existing list. Created with the
  // relevant flag preset false; the other flag is left at the backend's
  // default (true) since this form has no opinion on it.
  const [showNewCategory, setShowNewCategory] = useState(false)
  const [newCategoryForm, setNewCategoryForm] = useState(emptyNewCategoryForm)
  const [newCategoryErrors, setNewCategoryErrors] = useState({})
  const setNewCategoryField = (k, v) => {
    setNewCategoryForm((p) => ({ ...p, [k]: v }))
    setNewCategoryErrors((p) => ({ ...p, [k]: null }))
  }
  const [createBudgetCategory, { isLoading: creatingCategory }] = useCreateBudgetCategoryMutation()
  // Same cache-lag workaround used elsewhere (e.g. CreatePurchaseContainer's
  // rfqSupplierOptions): a just-created category isn't guaranteed to be in
  // `categories` until its list query refetches.
  const [createdCategories, setCreatedCategories] = useState([])
  const categoryOptions = useMemo(() => {
    const byId = new Map((categories || []).map((c) => [c.id, c]))
    createdCategories.forEach((c) => {
      if (!byId.has(c.id)) byId.set(c.id, c)
    })
    return Array.from(byId.values()).map((c) => ({ value: c.id, label: c.name }))
  }, [categories, createdCategories])

  const handleSaveNewCategory = async () => {
    if (!newCategoryForm.name.trim()) return
    const errs = validateFields(newCategoryForm, RULES.budgetCategory)
    setNewCategoryErrors(errs)
    if (hasErrors(errs)) return
    try {
      const created = await createBudgetCategory({
        code: newCategoryForm.code.trim() || undefined,
        name: newCategoryForm.name.trim(),
        assetClassCode: newCategoryForm.assetClassCode.trim() || undefined,
        sunPlAccount: newCategoryForm.sunPlAccount.trim() || undefined,
        sunBsAccount: newCategoryForm.sunBsAccount.trim() || undefined,
        ...flagParams,
      }).unwrap()
      setCreatedCategories((prev) => [...prev, created])
      showToastSuccess("Category created.")
      setShowNewCategory(false)
      setNewCategoryForm(emptyNewCategoryForm)
      patch({ budgetCategoryId: created.id })
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't create this category. Please try again."))
    }
  }

  // "Central" isn't a real hub.center_region bucket — same omission the
  // Order Request form uses (OrderRequestFormContainer.js:406-413).
  const { data: centersPage } = useGetProcurementCentersQuery({
    region: value.region === "North" || value.region === "South" ? value.region : undefined,
    size: 200,
  })
  const centerOptions = useMemo(
    () =>
      (centersPage?.items || []).map((c) => ({
        value: c.centerId,
        label: c.sunCenterCode ? `${c.centerName} (${c.sunCenterCode})` : c.centerName,
      })),
    [centersPage]
  )

  const { data: departmentsRaw } = useGetDepartmentsQuery()
  const departmentOptions = useMemo(() => {
    const list = Array.isArray(departmentsRaw) ? departmentsRaw : departmentsRaw?.data || []
    return list.map((d) => ({ value: d.teamId ?? d.id, label: d.teamName || d.name }))
  }, [departmentsRaw])

  const patch = (fields) => onChange({ ...value, ...fields })

  const updateLine = (idx, linePatch) =>
    patch({ lines: value.lines.map((l, i) => (i === idx ? { ...l, ...linePatch } : l)) })
  const addLine = () => patch({ lines: [...value.lines, newDirectLine(value.centerId)] })
  const removeLine = (idx) => patch({ lines: value.lines.filter((_, i) => i !== idx) })

  const total = computeLinesTotal(value.lines)

  return (
    <div className="pc-root" style={{ minHeight: "auto", background: "transparent" }}>
      <Card title="Direct spend details" subtitle="No Order Request — this builds one behind the scenes.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <Input
            label="Title"
            required
            placeholder="e.g. Figma Organization renewal"
            value={value.title}
            error={errors.title}
            onChange={(e) => patch({ title: e.target.value })}
            disabled={disabled}
          />
          <div>
            <Select
              label="Budget Category"
              required
              value={value.budgetCategoryId}
              onChange={(v) => patch({ budgetCategoryId: v })}
              options={categoryOptions}
              disabled={disabled || categoriesLoading || showNewCategory}
              placeholder={categoriesLoading ? "Loading…" : "Select category…"}
            />
            {!showNewCategory ? (
              <Button
                variant="text"
                size="sm"
                onClick={() => setShowNewCategory(true)}
                disabled={disabled}
                style={{ marginTop: 6 }}
              >
                + Create new category
              </Button>
            ) : (
              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  border: "1px dashed var(--pc-border)",
                  borderRadius: "var(--pc-r-md)",
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                  <Input
                    label="Code"
                    value={newCategoryForm.code}
                    error={newCategoryErrors.code}
                    onChange={(e) => setNewCategoryField("code", e.target.value)}
                    disabled={creatingCategory}
                  />
                  <Input
                    label="Name"
                    required
                    value={newCategoryForm.name}
                    error={newCategoryErrors.name}
                    onChange={(e) => setNewCategoryField("name", e.target.value)}
                    disabled={creatingCategory}
                  />
                  <Input
                    label="Asset class code"
                    value={newCategoryForm.assetClassCode}
                    error={newCategoryErrors.assetClassCode}
                    onChange={(e) => setNewCategoryField("assetClassCode", e.target.value)}
                    disabled={creatingCategory}
                  />
                  <Input
                    label="SUN P&L account"
                    value={newCategoryForm.sunPlAccount}
                    error={newCategoryErrors.sunPlAccount}
                    onChange={(e) => setNewCategoryField("sunPlAccount", e.target.value)}
                    disabled={creatingCategory}
                  />
                  <Input
                    label="SUN BS account"
                    value={newCategoryForm.sunBsAccount}
                    error={newCategoryErrors.sunBsAccount}
                    onChange={(e) => setNewCategoryField("sunBsAccount", e.target.value)}
                    disabled={creatingCategory}
                  />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveNewCategory}
                    disabled={creatingCategory || !newCategoryForm.name.trim()}
                  >
                    {creatingCategory ? "Creating…" : "Create category"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowNewCategory(false)
                      setNewCategoryForm(emptyNewCategoryForm)
                    }}
                    disabled={creatingCategory}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
          <Select
            label="Region"
            value={value.region}
            onChange={(v) => patch({ region: v })}
            options={REGION_OPTIONS}
          />
          <Select
            label="Center"
            required
            value={value.centerId}
            onChange={(v) => patch({ centerId: v })}
            options={centerOptions}
            disabled={disabled}
            hint="Also used as the default center for new lines."
          />
          <Select
            label="Department"
            value={value.departmentId}
            onChange={(v) => patch({ departmentId: v })}
            options={departmentOptions}
            disabled={disabled}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Textarea
            label="Reason"
            placeholder="Why is this purchase needed?"
            value={value.reason}
            onChange={(e) => patch({ reason: e.target.value })}
            disabled={disabled}
          />
        </div>
      </Card>

      <Card
        title="Line items"
        subtitle="At least 1 line — the total below is what the hidden order (and the budget commit) is based on."
        action={
          <Button variant="secondary" size="sm" leftIcon={<Plus aria-hidden />} onClick={addLine} disabled={disabled}>
            Add line
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {value.lines.map((line, idx) => (
            <OrderLineRow
              key={line.key}
              line={line}
              idx={idx}
              centerOptions={centerOptions}
              categoryId={value.budgetCategoryId || undefined}
              mustHaveOrder={mode === "order" ? false : undefined}
              mustHavePurchase={mode === "purchase" ? false : undefined}
              showDeliveryContact={false}
              showProofImage={false}
              onUpdate={updateLine}
              onRemove={removeLine}
              canRemove={value.lines.length > 1}
              disabled={disabled}
              errors={errors.lines[idx]}
            />
          ))}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            alignItems: "baseline",
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--pc-border)",
          }}
        >
          <span className="pc-stat-label">Lines total</span>
          <Currency value={total} strong />
        </div>
      </Card>
    </div>
  )
}

export default DirectSourceBuilder
